import { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import TooltipButton from "../../../common/tooltipButton";
import { TabMenu } from "primereact/tabmenu";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants } from "../../../../constants";
import {
  addLicenseRequest,
  enterpriseActivationRequest,
  enterpriseDetailsRequest,
  updateEntitlementsRequest,
} from "../../../../store/slices/enterpriseSlice";
import Form from "../../../common/form";
import EnterpriseDetailsForm from "./EnterpriseDetailsForm";
import EnterpriseLicenseDetails from "./EnterpriseLicenseDetails";
import EnterpriseStatusTag from "./EnterpriseStatusTag";
import AddLicenseDialog from "./AddLicenseDialog";
import EditEntitlementsDialog from "./EditEntitlementsDialog";
import {
  ENTITLEMENT_STORAGE,
  ENTITLEMENT_USERS,
  bytesToGb,
  currentLicense,
  entitlementValue,
  toLicenseDay,
  toLicenseRequest,
} from "../../../../helpers/EnterpriseLicenseHelper";
import CustomBreadCrumbs from "../../../common/breadcrumbs";

// An organisation's details for the super admin. Basic info is read-only;
// the super admin can switch the organisation off and on, add a licence and
// change its entitlements.
export const EnterpriseDetails = ({
  record,
  labels,
  parent,
  user,
  enterpriseDetailsRequest,
  addLicenseRequest,
  updateEntitlementsRequest,
  enterpriseActivationRequest,
  licenseSaving,
  licenseError,
  entitlementsSaving,
  entitlementsError,
  activating,
}) => {
  const intl = useIntl();
  const params = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [dialog, setDialog] = useState(null);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.TenantAccess,
    AccessScopeConstants.UpdateScope
  );

  useEffect(() => {
    enterpriseDetailsRequest({ id: params.id });
  }, [enterpriseDetailsRequest, params.id]);

  const closeDialog = useCallback(() => setDialog(null), []);

  const users = entitlementValue(record?.entitlements, ENTITLEMENT_USERS);
  const storage = entitlementValue(record?.entitlements, ENTITLEMENT_STORAGE);

  // The licence in force; when none is, the latest one (the API's license,
  // the one ending last), titled so.
  const current = useMemo(
    () => currentLicense(record?.licenses),
    [record?.licenses]
  );
  const shownLicense = current || record?.license;

  const initialValues = useMemo(
    () => ({
      ...record,
      license: {
        startDate: toLicenseDay(shownLicense?.startDate),
        endDate: toLicenseDay(shownLicense?.endDate),
      },
      // Users first, then storage in GB: the read-only fields' order.
      entitlements: [
        { type: ENTITLEMENT_USERS, value: users },
        {
          type: ENTITLEMENT_STORAGE,
          value: storage == null ? "" : bytesToGb(storage) + "",
        },
      ],
    }),
    [record, shownLicense, users, storage]
  );

  // The latest licence (the one ending last); a new one starts from its end.
  const latestEnd = useMemo(
    () => toLicenseDay(record?.license?.endDate),
    [record?.license?.endDate]
  );

  // action is "activate" or "deactivate".
  const confirmActivation = (action) => {
    confirmDialog({
      header: intl.formatMessage(
        { id: `enterprise_${action}_header` },
        { name: record?.name }
      ),
      message: intl.formatMessage({ id: `enterprise_${action}_message` }),
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptLabel: intl.formatMessage({ id: action }),
      rejectLabel: intl.formatMessage({ id: "cancel" }),
      acceptClassName:
        action === "deactivate" ? "p-button-danger" : "p-button-primary",
      rejectClassName: "p-button-secondary",
      accept: () => enterpriseActivationRequest({ id: record.id, action }),
      reject: () => {},
    });
  };

  const activationAction = record?.isActive ? "deactivate" : "activate";

  return (
    <div>
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title flex align-items-center gap-3">
            <span>
              <span
                className="pi pi-arrow-left mr-2 cursor-pointer"
                onClick={(e) => navigate(parent.to)}
              ></span>
              {intl.formatMessage({ id: "enterprise_details" })}
            </span>
            <EnterpriseStatusTag
              status={record?.status}
              licenses={record?.licenses}
            />
          </div>
          {(record && (
            <div className="edexlms-page-actions">
              <TooltipButton
                data-testid={`enterprise-${activationAction}`}
                icon={record.isActive ? "pi pi-times" : "pi pi-check"}
                outlined
                severity={record.isActive ? "danger" : undefined}
                onClick={() => confirmActivation(activationAction)}
                label={intl.formatMessage({ id: activationAction })}
                type="button"
                size="small"
                loading={activating}
                disabled={!canUpdate || activating}
                tooltipOptions={{ position: "bottom" }}
                tooltip={
                  canUpdate ? "" : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body">
          <TabMenu
            model={[
              {
                label: intl.formatMessage({ id: "basic_info" }),
                command: () => setActiveTab(0),
              },
              {
                label: intl.formatMessage({ id: "enterprise_license" }),
                command: () => setActiveTab(1),
              },
            ]}
          />
          <div className="edexlms-page-body-content enterprise-view-details">
            {(activeTab === 0 && (
              <Form initialValues={initialValues} disabled>
                <EnterpriseDetailsForm disabled record={record} />
              </Form>
            )) ||
              null}
            {(activeTab === 1 && (
              <Form initialValues={initialValues} disabled>
                <EnterpriseLicenseDetails
                  canUpdate={canUpdate}
                  isCurrentLicense={Boolean(current)}
                  licenses={record?.licenses}
                  onAddLicense={() => setDialog("license")}
                  onEditEntitlements={() => setDialog("entitlements")}
                />
              </Form>
            )) ||
              null}
          </div>
        </div>
      </div>
      {/* Outside the read-only forms: a dialog's form must not submit them. */}
      <AddLicenseDialog
        visible={dialog === "license"}
        onHide={closeDialog}
        latestEnd={latestEnd}
        saving={licenseSaving}
        errorCode={licenseError}
        onSubmit={(values) =>
          addLicenseRequest({ id: record.id, ...toLicenseRequest(values) })
        }
      />
      <EditEntitlementsDialog
        visible={dialog === "entitlements"}
        onHide={closeDialog}
        users={users}
        storage={storage}
        saving={entitlementsSaving}
        errorCode={entitlementsError}
        onSubmit={(values) =>
          updateEntitlementsRequest({ id: record.id, ...values })
        }
      />
      <ConfirmDialog />
    </div>
  );
};

const mapStateToProps = (state) => ({
  record: state?.enterprises?.record,
  user: state?.self?.user,
  licenseSaving: state?.enterprises?.licenseSaving,
  licenseError: state?.enterprises?.licenseError,
  entitlementsSaving: state?.enterprises?.entitlementsSaving,
  entitlementsError: state?.enterprises?.entitlementsError,
  activating: state?.enterprises?.activating,
});

const mapDispatchToProps = (dispatch) => ({
  enterpriseDetailsRequest: (data) => dispatch(enterpriseDetailsRequest(data)),
  addLicenseRequest: (data) => dispatch(addLicenseRequest(data)),
  updateEntitlementsRequest: (data) =>
    dispatch(updateEntitlementsRequest(data)),
  enterpriseActivationRequest: (data) =>
    dispatch(enterpriseActivationRequest(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnterpriseDetails);
